import AddIcon from "@mui/icons-material/Add";
import { Box, Button, Stack } from "@mui/material";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  type NotSerializedBookingInputs,
  type VisitorRegistrationEntry
} from "../../../features/Booking-Form/typings/booking-inputs";
import type { TeamMember } from "../../../features/Booking-Form/typings/team-member";
import {
  createEmptyVisitorRegistrationEntry,
  getDuplicateVisitorEmailIds,
  isVisitorRegistrationStepComplete
} from "../../../features/Booking-Form/functions/visitor-registration.functions";
import { visitorRegistrationEntryToTeamBookingGuest } from "../functions/team-visitor-invitation.functions";
import { VisitorRegistrationRow } from "../visitor-registration-row.component";

export type TeamVisitorInvitationConfirmPayload = {
  visitorRegistrations: VisitorRegistrationEntry[];
  guests: TeamMember[];
};

export type TeamVisitorInvitationRegistrationsPanelProps = {
  visible: boolean;
  bookerSub: string;
  initialRegistrations?: VisitorRegistrationEntry[];
  visitOnlyOnly?: boolean;
  /** Conference invite: hide per-visitor location; building is derived from the selected room. */
  hideVisitOnlyLocation?: boolean;
  derivedVisitOnlyLocationLabel?: string | null;
  /** When false (conference invite wizard), draft syncs via `onDraftChange` without Save/Cancel. */
  showFooterActions?: boolean;
  onConfirm?: (payload: TeamVisitorInvitationConfirmPayload) => void;
  onDraftChange?: (visitorRegistrations: VisitorRegistrationEntry[]) => void;
  onCancel?: () => void;
  /** Root `data-testid` for the bordered panel */
  panelDataTestId?: string;
  /** Override scroll shell max height */
  maxHeight?: string;
};

/**
 * Shared visitor registration rows + add row + cancel/confirm for team booking and conference zone flows.
 * Keeps draft state internally while `visible`; seeds from `initialRegistrations` when opened.
 */
export function TeamVisitorInvitationRegistrationsPanel({
  visible,
  bookerSub,
  initialRegistrations,
  visitOnlyOnly = false,
  hideVisitOnlyLocation = false,
  derivedVisitOnlyLocationLabel = null,
  showFooterActions = true,
  onConfirm,
  onDraftChange,
  onCancel,
  panelDataTestId = "team-visitor-invitation-panel",
  maxHeight = "min(52vh, 420px)"
}: Readonly<TeamVisitorInvitationRegistrationsPanelProps>) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const wasVisibleRef = useRef(false);
  const [visitorRegistrations, setVisitorRegistrations] = useState<VisitorRegistrationEntry[]>(
    () =>
      initialRegistrations && initialRegistrations.length > 0
        ? initialRegistrations
        : [createEmptyVisitorRegistrationEntry()]
  );

  useEffect(() => {
    const justOpened = visible && !wasVisibleRef.current;
    wasVisibleRef.current = visible;
    if (justOpened) {
      setVisitorRegistrations(
        initialRegistrations && initialRegistrations.length > 0
          ? initialRegistrations
          : [createEmptyVisitorRegistrationEntry()]
      );
    }
  }, [initialRegistrations, visible]);

  const duplicateEmailIds = getDuplicateVisitorEmailIds({
    visitorRegistrations
  } as unknown as NotSerializedBookingInputs);

  const patchVisitor = useCallback((id: string, patch: Partial<VisitorRegistrationEntry>) => {
    setVisitorRegistrations(prev => prev.map(row => (row.id === id ? { ...row, ...patch } : row)));
  }, []);

  const addVisitor = useCallback(() => {
    setVisitorRegistrations(prev => {
      const first = prev[0];
      const newEntry: VisitorRegistrationEntry = {
        ...createEmptyVisitorRegistrationEntry(),
        company: (first?.company ?? "").trim(),
        visitReason: (first?.visitReason ?? "").trim()
      };
      return [...prev, newEntry];
    });
  }, []);

  const removeVisitor = useCallback((id: string) => {
    setVisitorRegistrations(prev => {
      if (prev.length <= 1) {
        return prev;
      }
      return prev.filter(row => row.id !== id);
    });
  }, []);

  const normalizedRegistrations = useMemo(
    () =>
      visitOnlyOnly
        ? visitorRegistrations.map(row => ({ ...row, bookPlace: false }))
        : visitorRegistrations,
    [visitOnlyOnly, visitorRegistrations]
  );

  useEffect(() => {
    if (!visible || !onDraftChange) {
      return;
    }
    onDraftChange(normalizedRegistrations);
  }, [normalizedRegistrations, onDraftChange, visible]);

  const handleConfirm = () => {
    const payload = {
      visitorRegistrations: normalizedRegistrations
    } as unknown as NotSerializedBookingInputs;
    if (!isVisitorRegistrationStepComplete(payload)) {
      enqueueSnackbar(t("_visitorBooking_teamVisitorInvitationIncomplete"), { variant: "warning" });
      return;
    }
    const guests = normalizedRegistrations.map(row =>
      visitorRegistrationEntryToTeamBookingGuest(row, bookerSub)
    );
    onConfirm?.({
      visitorRegistrations: normalizedRegistrations,
      guests
    });
  };

  if (!visible) {
    return null;
  }

  return (
    <Box
      data-testid={panelDataTestId}
      sx={{
        width: "100%",
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        display: "flex",
        flexDirection: "column",
        maxHeight,
        overflow: "hidden"
      }}
    >
      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", px: 2, py: 2 }}>
        <Stack spacing={3}>
          {visitorRegistrations.map((entry, index) => (
            <VisitorRegistrationRow
              key={entry.id}
              canRemove={visitorRegistrations.length > 1}
              emailDuplicate={duplicateEmailIds.has(entry.id)}
              entry={entry}
              derivedVisitOnlyLocationLabel={derivedVisitOnlyLocationLabel}
              hideBookPlaceSwitch={visitOnlyOnly}
              hideVisitOnlyLocation={hideVisitOnlyLocation}
              index={index}
              onPatch={patchVisitor}
              onRemove={removeVisitor}
            />
          ))}
          <Button
            color="primary"
            data-testid="team-visitor-invitation-add-row"
            onClick={addVisitor}
            startIcon={<AddIcon />}
            sx={{ alignSelf: "flex-start", textTransform: "none" }}
            variant="outlined"
          >
            {t("_visitorBooking_addVisitor")}
          </Button>
        </Stack>
      </Box>
      {showFooterActions && (
        <Stack
          direction="row"
          justifyContent="flex-end"
          spacing={1}
          sx={{
            flexShrink: 0,
            px: 2,
            py: 2,
            borderTop: 1,
            borderColor: "divider",
            bgcolor: "background.paper"
          }}
        >
          {onCancel && (
            <Button
              data-testid="team-visitor-invitation-cancel"
              onClick={onCancel}
              color="secondary"
            >
              {t("Cancel")}
            </Button>
          )}
          <Button data-testid="team-visitor-invitation-confirm" onClick={handleConfirm}>
            {t("Save")}
          </Button>
        </Stack>
      )}
    </Box>
  );
}
